import React, { useState, useEffect } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Icon from "@mui/material/Icon";
import MDInput from "components/MDInput";
import PropTypes from "prop-types";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import CircularProgress from "@mui/material/CircularProgress";
import axios from "axios";
import { startPaystackPayment, startStripePayment } from "services/payment";
import { adminApi } from "services/api";
import { lightDark, darkTint, DARK } from "utils/lightDark";

// Helper for region detection (to be implemented)
const detectRegion = async () => {
  // Use a free IP geolocation API or fallback to user selection
  // Example: https://ipapi.co/json/
  try {
    const res = await fetch("https://ipapi.co/json/");
    const data = await res.json();
    return data.country_name;
  } catch {
    return null;
  }
};

const regionCurrencyMap = {
  Nigeria: { gateway: "paystack", currency: "NGN", allowChoice: false },
  UK: { gateway: "stripe", currency: "GBP", allowChoice: true },
  USA: { gateway: "stripe", currency: "USD", allowChoice: true },
  EU: { gateway: "stripe", currency: "EUR", allowChoice: true },
  "South Africa": { gateway: "stripe", currency: "USD", allowChoice: true },
};

const supportedCurrencies = ["USD", "EUR", "GBP"];

const BootcampDetail = ({ bootcamp }) => {
  const [region, setRegion] = useState("");
  const [currency, setCurrency] = useState("");
  const [exchangeRate, setExchangeRate] = useState(1);
  const [loading, setLoading] = useState(true);
  const [price, setPrice] = useState(bootcamp.price);
  const [error, setError] = useState("");

  // For visitors: collect email/name before payment
  const [userInfo, setUserInfo] = useState({ email: "", name: "" });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogType, setDialogType] = useState(""); // "pay" or "plan"

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      let detectedRegion = await detectRegion();
      if (!regionCurrencyMap[detectedRegion]) detectedRegion = "USA";
      setRegion(detectedRegion);
      let defaultCurrency = regionCurrencyMap[detectedRegion]?.currency || "USD";
      setCurrency(defaultCurrency);
      await fetchExchangeRate(defaultCurrency);
      setLoading(false);
    };
    init();
    // eslint-disable-next-line
  }, [bootcamp.price]);

  const fetchExchangeRate = async (targetCurrency) => {
    // If target currency is USD, no conversion needed
    if (targetCurrency === "USD") {
      setExchangeRate(1);
      setPrice(bootcamp.price);
      console.log("Currency is USD, price:", bootcamp.price);
      return;
    }
    try {
      // Convert from USD (base price) to target currency using exchangerate-api.com
      console.log(`Fetching exchange rate from USD to ${targetCurrency}`);
      const res = await axios.get(`https://api.exchangerate-api.com/v4/latest/USD`);
      const rate = res.data.rates[targetCurrency];
      console.log(`Exchange rate for ${targetCurrency}:`, rate);
      if (!rate) {
        throw new Error(`No rate found for ${targetCurrency}`);
      }
      const convertedPrice = Math.round(bootcamp.price * rate);
      console.log(`Converted price: ${bootcamp.price} USD -> ${convertedPrice} ${targetCurrency}`);
      setExchangeRate(rate);
      setPrice(convertedPrice);
    } catch (err) {
      console.error("Exchange rate fetch failed:", err);
      setError("Failed to fetch exchange rate");
      setExchangeRate(1);
      setPrice(bootcamp.price);
    }
  };

  const handleCurrencyChange = async (e) => {
    const newCurrency = e.target.value;
    setCurrency(newCurrency);
    await fetchExchangeRate(newCurrency);
  };

  if (loading) return <CircularProgress />;

  return (
    <MDBox>
      <MDTypography variant="h4" sx={{ overflowWrap: "anywhere" }}>
        {bootcamp.title}
      </MDTypography>
      <MDTypography
        variant="body1"
        sx={{
          overflowWrap: "anywhere",
          "& img, & video, & iframe": { maxWidth: "100%", height: "auto" },
        }}
        dangerouslySetInnerHTML={{ __html: bootcamp.description }}
      />

      {/* Bootcamp Details */}
      <MDBox mt={3}>
        <MDTypography variant="h5" mb={2}>
          Bootcamp Details
        </MDTypography>
        <MDBox
          display="grid"
          gridTemplateColumns="repeat(auto-fit, minmax(min(200px, 100%), 1fr))"
          gap={2}
        >
          <MDBox>
            <MDTypography variant="caption" color="text" fontWeight="bold">
              Duration
            </MDTypography>
            <MDTypography variant="body2">{bootcamp.duration || "N/A"}</MDTypography>
          </MDBox>
          <MDBox>
            <MDTypography variant="caption" color="text" fontWeight="bold">
              Level
            </MDTypography>
            <MDTypography variant="body2">{bootcamp.level || "N/A"}</MDTypography>
          </MDBox>
          <MDBox>
            <MDTypography variant="caption" color="text" fontWeight="bold">
              Class Size
            </MDTypography>
            <MDTypography variant="body2">{bootcamp.classSize || "N/A"}</MDTypography>
          </MDBox>
          <MDBox>
            <MDTypography variant="caption" color="text" fontWeight="bold">
              Format
            </MDTypography>
            <MDTypography variant="body2">{bootcamp.format || "N/A"}</MDTypography>
          </MDBox>
          <MDBox>
            <MDTypography variant="caption" color="text" fontWeight="bold">
              Certificate
            </MDTypography>
            <MDTypography variant="body2">{bootcamp.certificate ? "Yes" : "No"}</MDTypography>
          </MDBox>
        </MDBox>
      </MDBox>

      {/* Curriculum */}
      {bootcamp.curriculum && bootcamp.curriculum.length > 0 && (
        <MDBox mt={3}>
          <MDTypography variant="h5" mb={2}>
            Curriculum
          </MDTypography>
          <ul style={{ paddingLeft: "20px" }}>
            {bootcamp.curriculum.map((item, index) => (
              <li key={index}>
                <MDTypography variant="body2">{item}</MDTypography>
              </li>
            ))}
          </ul>
        </MDBox>
      )}

      {/* Instructors */}
      {bootcamp.instructors && bootcamp.instructors.length > 0 && (
        <MDBox mt={3}>
          <MDTypography variant="h5" mb={2}>
            Instructors
          </MDTypography>
          <MDBox
            display="grid"
            gridTemplateColumns="repeat(auto-fit, minmax(min(250px, 100%), 1fr))"
            gap={3}
          >
            {bootcamp.instructors.map((instructor, index) => (
              <MDBox
                key={index}
                p={2}
                sx={{
                  border: lightDark("1px solid #e0e0e0", `1px solid ${DARK.border}`),
                  borderRadius: "8px",
                }}
              >
                {instructor.image && (
                  <img
                    src={instructor.image}
                    alt={instructor.name}
                    style={{
                      width: "100%",
                      height: "150px",
                      objectFit: "cover",
                      borderRadius: "8px",
                      marginBottom: "10px",
                    }}
                  />
                )}
                <MDTypography variant="h6">{instructor.name}</MDTypography>
                {instructor.title && (
                  <MDTypography variant="caption" color="text" display="block">
                    {instructor.title}
                  </MDTypography>
                )}
                {instructor.bio && (
                  <MDTypography variant="body2" mt={1}>
                    {instructor.bio}
                  </MDTypography>
                )}
                {instructor.linkedIn && (
                  <MDBox mt={1}>
                    <a href={instructor.linkedIn} target="_blank" rel="noopener noreferrer">
                      <MDTypography variant="button" color="info">
                        LinkedIn Profile
                      </MDTypography>
                    </a>
                  </MDBox>
                )}
              </MDBox>
            ))}
          </MDBox>
        </MDBox>
      )}

      {/* What's Included - Job Stats */}
      {(bootcamp.jobPlacementRate > 0 ||
        bootcamp.averageStartingSalary > 0 ||
        bootcamp.averageTimeToHire) && (
        <MDBox
          mt={3}
          p={{ xs: 2, md: 3 }}
          sx={{ backgroundColor: lightDark("#f5f5f5", DARK.surface), borderRadius: "8px" }}
        >
          <MDTypography variant="h5" mb={2}>
            What's Included
          </MDTypography>
          <MDBox
            display="grid"
            gridTemplateColumns="repeat(auto-fit, minmax(min(200px, 100%), 1fr))"
            gap={2}
          >
            {bootcamp.jobPlacementRate > 0 && (
              <MDBox textAlign="center">
                <MDTypography variant="h3" color="success">
                  {bootcamp.jobPlacementRate}%
                </MDTypography>
                <MDTypography variant="caption" color="text">
                  Job Placement Rate
                </MDTypography>
              </MDBox>
            )}
            {bootcamp.averageStartingSalary > 0 && (
              <MDBox textAlign="center">
                <MDTypography variant="h3" color="info">
                  ${bootcamp.averageStartingSalary.toLocaleString()}
                </MDTypography>
                <MDTypography variant="caption" color="text">
                  Average Starting Salary
                </MDTypography>
              </MDBox>
            )}
            {bootcamp.averageTimeToHire && bootcamp.averageTimeToHire !== "N/A" && (
              <MDBox textAlign="center">
                <MDTypography variant="h3" color="warning">
                  {bootcamp.averageTimeToHire}
                </MDTypography>
                <MDTypography variant="caption" color="text">
                  Average Time to Hire
                </MDTypography>
              </MDBox>
            )}
          </MDBox>
        </MDBox>
      )}

      {/* Price and Payment */}
      <MDTypography variant="h6" mt={2}>
        Price: {price} {currency}
      </MDTypography>
      {regionCurrencyMap[region]?.allowChoice && (
        <Select value={currency} onChange={handleCurrencyChange}>
          {supportedCurrencies.map((cur) => (
            <MenuItem key={cur} value={cur}>
              {cur}
            </MenuItem>
          ))}
        </Select>
      )}
      <MDButton
        color="info"
        sx={{ mt: 2, width: { xs: "100%", sm: "auto" } }}
        onClick={() => {
          setDialogType("pay");
          setDialogOpen(true);
        }}
      >
        Pay Now
      </MDButton>
      <MDButton
        color="secondary"
        sx={{ mt: 2, ml: { xs: 0, sm: 2 }, width: { xs: "100%", sm: "auto" } }}
        onClick={() => {
          setDialogType("plan");
          setDialogOpen(true);
        }}
      >
        Pay via Payment Plan
      </MDButton>
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "16px",
            boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
          },
        }}
      >
        <DialogTitle
          sx={{
            background: "linear-gradient(135deg, #00A8E8 0%, #007EA7 100%)",
            color: "white",
            padding: { xs: "20px", sm: "24px 30px" },
            display: "flex",
            alignItems: "center",
            gap: "12px",
            fontWeight: 600,
            fontSize: { xs: "1.25rem", sm: "1.5rem" },
          }}
        >
          <Icon sx={{ fontSize: "1.8rem" }}>
            {dialogType === "pay" ? "credit_card" : "calendar_month"}
          </Icon>
          {dialogType === "pay" ? "Complete Your Payment" : "Request Payment Plan"}
        </DialogTitle>
        <DialogContent sx={{ padding: { xs: "20px", sm: "30px" } }}>
          <MDBox sx={{ mb: 3, mt: 2 }}>
            <MDTypography
              variant="caption"
              fontWeight="bold"
              color="text"
              sx={{
                mb: 1,
                display: "flex",
                alignItems: "center",
                gap: "6px",
                fontSize: "0.95rem",
              }}
            >
              <Icon sx={{ fontSize: "1rem" }}>person</Icon>
              Full Name
            </MDTypography>
            <MDInput
              placeholder="John Doe"
              value={userInfo.name}
              onChange={(e) => setUserInfo({ ...userInfo, name: e.target.value })}
              fullWidth
              required
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "10px",
                  "& fieldset": {
                    borderWidth: "2px",
                    borderColor: lightDark("#e1e8ed", DARK.border),
                  },
                  "&:hover fieldset": {
                    borderColor: "#00A8E8",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#00A8E8",
                  },
                },
                "& .MuiOutlinedInput-input": {
                  padding: "14px 16px",
                },
              }}
            />
          </MDBox>
          <MDBox sx={{ mb: 3 }}>
            <MDTypography
              variant="caption"
              fontWeight="bold"
              color="text"
              sx={{
                mb: 1,
                display: "flex",
                alignItems: "center",
                gap: "6px",
                fontSize: "0.95rem",
              }}
            >
              <Icon sx={{ fontSize: "1rem" }}>email</Icon>
              Email Address
            </MDTypography>
            <MDInput
              type="email"
              placeholder="john@example.com"
              value={userInfo.email}
              onChange={(e) => setUserInfo({ ...userInfo, email: e.target.value })}
              fullWidth
              required
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "10px",
                  "& fieldset": {
                    borderWidth: "2px",
                    borderColor: lightDark("#e1e8ed", DARK.border),
                  },
                  "&:hover fieldset": {
                    borderColor: "#00A8E8",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#00A8E8",
                  },
                },
                "& .MuiOutlinedInput-input": {
                  padding: "14px 16px",
                },
              }}
            />
          </MDBox>
          <MDBox
            sx={{
              background: lightDark(
                "linear-gradient(135deg, #e8f5f9 0%, #d4ebf7 100%)",
                darkTint("#00A8E8", 0.12)
              ),
              borderLeft: "4px solid #00A8E8",
              padding: "16px 20px",
              borderRadius: "10px",
              display: "flex",
              gap: "12px",
              alignItems: "flex-start",
            }}
          >
            <Icon sx={{ color: "#00A8E8", fontSize: "1.4rem", mt: 0.2 }}>info</Icon>
            <MDBox>
              <MDTypography
                variant="button"
                fontWeight="bold"
                color="info"
                sx={{ display: "block", mb: 0.5 }}
              >
                {dialogType === "pay" ? "Secure Payment" : "Quick Response"}
              </MDTypography>
              <MDTypography variant="caption" color="text" sx={{ fontSize: "0.9rem" }}>
                {dialogType === "pay"
                  ? "You'll be redirected to our secure payment gateway to complete your transaction."
                  : "Our team will contact you within 24 hours to customize your payment plan."}
              </MDTypography>
            </MDBox>
          </MDBox>
        </DialogContent>
        <DialogActions
          sx={{
            padding: { xs: "16px 20px", sm: "20px 30px" },
            borderTop: lightDark("1px solid #e1e8ed", `1px solid ${DARK.border}`),
            gap: "12px",
            flexDirection: { xs: "column-reverse", sm: "row" },
            alignItems: { xs: "stretch", sm: "center" },
            "& > :not(style) ~ :not(style)": { ml: { xs: 0, sm: 1 } },
          }}
        >
          <MDButton
            onClick={() => setDialogOpen(false)}
            sx={{
              background: lightDark("#e1e8ed", DARK.surfaceHover),
              color: lightDark("#2c3e50", DARK.text),
              padding: "10px 24px",
              borderRadius: "8px",
              fontWeight: 600,
              "&:hover": {
                background: lightDark("#cbd5de", "rgba(255, 255, 255, 0.14)"),
                transform: "translateY(-1px)",
              },
              // MDButton's focus style paints white.focus, which hid the light label in dark mode.
              "&:focus:not(:hover)": {
                backgroundColor: lightDark("#ffffff", DARK.surfaceHover),
              },
            }}
          >
            <Icon sx={{ mr: 1, fontSize: "1.1rem" }}>close</Icon>
            Cancel
          </MDButton>
          <MDButton
            onClick={async () => {
              setError("");
              if (!userInfo.email || !userInfo.name) {
                setError("Name and email are required");
                return;
              }
              setDialogOpen(false);
              if (dialogType === "pay") {
                try {
                  if (regionCurrencyMap[region]?.gateway === "paystack") {
                    // Use server-side Paystack initialization for webhook support
                    const { data } = await axios.post("/api/payment/paystack-initialize", {
                      bootcampId: bootcamp._id,
                      email: userInfo.email,
                      name: userInfo.name,
                      amount: price,
                      currency,
                    });
                    // Redirect to Paystack payment page
                    window.location.href = data.authorizationUrl;
                  } else {
                    const {
                      data: { sessionUrl },
                    } = await axios.post("/api/payment/stripe-session", {
                      bootcampId: bootcamp._id,
                      email: userInfo.email,
                      name: userInfo.name,
                      amount: price,
                      currency,
                    });
                    await startStripePayment({ sessionUrl });
                  }
                } catch (err) {
                  setError(err.message || "Payment failed");
                }
              } else if (dialogType === "plan") {
                try {
                  await adminApi.requestPaymentPlan({
                    bootcampId: bootcamp._id,
                    userId: undefined,
                    country: region,
                    currency,
                    email: userInfo.email,
                    name: userInfo.name,
                  });
                  alert("Payment plan requested! Admin will contact you.");
                } catch (err) {
                  setError(err.message || "Payment plan request failed");
                }
              }
            }}
            sx={{
              background: "linear-gradient(135deg, #00A8E8 0%, #007EA7 100%)",
              color: "white",
              padding: "10px 24px",
              borderRadius: "8px",
              fontWeight: 600,
              "&:hover": {
                transform: "translateY(-2px)",
                boxShadow: "0 6px 20px rgba(0,168,232,0.4)",
              },
            }}
          >
            <Icon sx={{ mr: 1, fontSize: "1.1rem" }}>{dialogType === "pay" ? "lock" : "send"}</Icon>
            {dialogType === "pay" ? "Proceed to Payment" : "Submit Request"}
          </MDButton>
        </DialogActions>
      </Dialog>
      {error && <MDTypography color="error">{error}</MDTypography>}
    </MDBox>
  );
};

BootcampDetail.propTypes = {
  bootcamp: PropTypes.object.isRequired,
};

export default BootcampDetail;
